import { useEffect, useRef, useCallback } from "react";
import PropTypes from 'prop-types';
import styles from './PopUpModal.module.css';
import closeIcon from './assets/close.svg';

/**
 * PopUpModal - A reusable modal dialog component
 * 
 * @param {Object} props
 * @param {Function} props.handleClose - Callback when modal closes
 * @param {React.ReactNode} props.children - Modal content
 * @param {string} props.size - Modal size: 'small', 'medium', 'large', 'fullscreen'
 * @param {boolean} props.showCloseButton - Show/hide close button (default: true)
 * @param {boolean} props.closeOnBackdropClick - Close modal when clicking backdrop (default: true)
 * @param {boolean} props.closeOnEscape - Close modal on ESC key (default: true)
 * @param {string} props.className - Additional CSS class
 * @param {string} props.title - Accessible title for screen readers
 * @param {boolean} props.preventBodyScroll - Prevent body scroll when modal is open (default: true)
 */
export default function PopUpModal({
    handleClose,
    children,
    size = 'medium',
    showCloseButton = true,
    closeOnBackdropClick = true,
    closeOnEscape = true,
    className = '',
    title = 'Modal Dialog',
    preventBodyScroll = true,
    ariaLabelledBy,
    ariaDescribedBy,
}) {
    const dialogRef = useRef(null);
    const previousActiveElement = useRef(null);

    // Handle close with callback
    const handleCloseModal = useCallback(() => {
        if (handleClose) {
            handleClose();
        }
    }, [handleClose]);

    // Handle backdrop click
    const handleBackdropClick = useCallback((e) => {
        if (closeOnBackdropClick && e.target === dialogRef.current) {
            handleCloseModal();
        }
    }, [closeOnBackdropClick, handleCloseModal]);

    // Handle escape key
    useEffect(() => {
        if (!closeOnEscape) return;

        const handleEscape = (e) => {
            if (e.key === 'Escape') {
                handleCloseModal();
            }
        };

        document.addEventListener('keydown', handleEscape);
        return () => document.removeEventListener('keydown', handleEscape);
    }, [closeOnEscape, handleCloseModal]);

    // Manage body scroll
    useEffect(() => {
        if (!preventBodyScroll) return;

        const originalOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';

        return () => {
            document.body.style.overflow = originalOverflow;
        };
    }, [preventBodyScroll]);

    // Focus management
    useEffect(() => {
        const dialog = dialogRef.current;
        if (!dialog) return;

        // Store currently focused element
        previousActiveElement.current = document.activeElement;

        // Show modal
        dialog.showModal();

        // Focus first focusable element or close button
        const focusableElements = dialog.querySelectorAll(
            'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        if (focusableElements.length > 0) {
            focusableElements[0].focus();
        }

        // Restore focus on unmount
        return () => {
            dialog.close();
            if (previousActiveElement.current) {
                previousActiveElement.current.focus();
            }
        };
    }, []);

    // Focus trap
    useEffect(() => {
        const dialog = dialogRef.current;
        if (!dialog) return;

        const handleTab = (e) => {
            if (e.key !== 'Tab') return;

            const focusableElements = dialog.querySelectorAll(
                'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
            );

            if (focusableElements.length === 0) return;

            const firstElement = focusableElements[0];
            const lastElement = focusableElements[focusableElements.length - 1];

            if (e.shiftKey && document.activeElement === firstElement) {
                e.preventDefault();
                lastElement.focus();
            } else if (!e.shiftKey && document.activeElement === lastElement) {
                e.preventDefault();
                firstElement.focus();
            }
        };

        dialog.addEventListener('keydown', handleTab);
        return () => dialog.removeEventListener('keydown', handleTab);
    }, []);

    const sizeClass = styles[size] || styles.medium;

    return (
        <dialog
            ref={dialogRef}
            className={`${styles.container} ${sizeClass} ${className}`}
            onClick={handleBackdropClick}
            aria-modal="true"
            aria-label={title}
            aria-labelledby={ariaLabelledBy}
            aria-describedby={ariaDescribedBy}
        >
            <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
                {showCloseButton && (
                    <button
                        className={styles.closeModalIcon}
                        onClick={handleCloseModal}
                        aria-label="Close modal"
                        type="button"
                    >
                        <img src={closeIcon} alt="" aria-hidden="true" />
                    </button>
                )}
                <div className={styles.contentMain}>
                    {children}
                </div>
            </div>
        </dialog>
    );
}

PopUpModal.propTypes = {
    handleClose: PropTypes.func.isRequired,
    children: PropTypes.node.isRequired,
    size: PropTypes.oneOf(['small', 'medium', 'large', 'fullscreen']),
    showCloseButton: PropTypes.bool,
    closeOnBackdropClick: PropTypes.bool,
    closeOnEscape: PropTypes.bool,
    className: PropTypes.string,
    title: PropTypes.string,
    preventBodyScroll: PropTypes.bool,
    ariaLabelledBy: PropTypes.string,
    ariaDescribedBy: PropTypes.string,
};
